import { ChangeEvent, useState } from 'react';
import {
    Box,
    TagCloseButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    Flex,
    Center,
    Input,
    IconButton,
    Icon,
    ModalFooter,
    Button,
    useToast,
    Tag,
} from '@chakra-ui/react';
import { useAddNewAliasMutation } from 'core/API/productsApi';
import { useTranslation } from 'helpers/languages/translations';
import { tagStyle } from 'shared/styles';
import icons from 'shared/icons';
import { TAliasProps } from '../../AliasContainer';

const AliasAddModal = ({ isOpenAdd, onCloseAdd }: TAliasProps) => {
    const translate = useTranslation();
    const toast = useToast();

    const [toAddAlias, setToAddAlias] = useState<{ mainAlias: string; aliases: string[] } | null>(null);
    const [mainAliasInputAdd, setMainAliasInputAdd] = useState('');
    const [aliasesInputAdd, setAliasesInputAdd] = useState('');

    const [addAliasQuery, { isLoading: isAddLoading }] = useAddNewAliasMutation();

    const handleAddInputChange = (event: ChangeEvent<HTMLInputElement>, key = true) => {
        if (key) {
            const val = event.target.value;
            setMainAliasInputAdd(val);
            const prevAliases = toAddAlias ? toAddAlias.aliases : [];
            setToAddAlias({ mainAlias: val, aliases: prevAliases });
        } else {
            setAliasesInputAdd(event.target.value);
        }
    };

    const handleAddNewAlias = () => {
        const newCurrentAlias = { mainAlias: toAddAlias!.mainAlias, aliases: [...toAddAlias!.aliases] };
        if (!newCurrentAlias.aliases.includes(aliasesInputAdd) && aliasesInputAdd !== newCurrentAlias.mainAlias) {
            newCurrentAlias.aliases.push(aliasesInputAdd);
            setToAddAlias(newCurrentAlias);
            setAliasesInputAdd('');
        }
    };

    const handleApplyAddNewAlias = async () => {
        try {
            const res = await addAliasQuery({
                key: toAddAlias!.mainAlias,
                aliases: toAddAlias!.aliases,
            });

            if ('error' in res) throw new Error(translate('alias.add.error') as string);

            setMainAliasInputAdd('');
            setToAddAlias({ mainAlias: '', aliases: [] });
            onCloseAdd();
            toast({
                title: translate('alias.add.success'),
                status: 'success',
                isClosable: true,
            });
        } catch (error: any) {
            const title =
                'data' in error ? (error as { data: { message: string } }).data.message : (error as Error).message;
            toast({
                title,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleDeleteAliasAdd = (alias: string) => {
        const newCurrentAlias = { mainAlias: toAddAlias!.mainAlias, aliases: [...toAddAlias!.aliases] };
        newCurrentAlias.aliases = newCurrentAlias.aliases.filter((e) => e !== alias);
        setToAddAlias(newCurrentAlias);
    };

    const tagList =
        toAddAlias &&
        toAddAlias.aliases.map((tagName) => (
            <Tag h="fit-content" {...tagStyle} key={tagName} mb="4px" mr="4px">
                {tagName}
                <TagCloseButton onClick={() => handleDeleteAliasAdd(tagName)} />
            </Tag>
        ));

    return (
        <Modal isOpen={isOpenAdd} onClose={onCloseAdd}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{translate('alias.add.header')}</ModalHeader>
                <ModalCloseButton disabled={isAddLoading} />
                <ModalBody>
                    <Box>
                        <Flex border="1px solid #CAC5C4" borderRadius="4px" minH="48px" mb="24px">
                            <Center
                                width="30%"
                                fontWeight="bold"
                                borderRight="1px solid #CAC5C4"
                                p={2}
                                wordBreak="break-word"
                            >
                                {toAddAlias?.mainAlias}
                            </Center>
                            <Flex flexWrap="wrap" justifyContent="flex-start" width="80%" p="8px">
                                {tagList}
                            </Flex>
                        </Flex>
                        <Flex alignItems="flex-end" justifyContent="space-between">
                            <Box flex="1" mr="16px">
                                <Input
                                    size="sm"
                                    mb="12px"
                                    value={mainAliasInputAdd}
                                    onChange={(event) => handleAddInputChange(event)}
                                    placeholder={translate('alias.add.nameSplit.placeholder') as string}
                                />
                                <Input
                                    size="sm"
                                    isDisabled={!toAddAlias || toAddAlias.mainAlias === ''}
                                    value={aliasesInputAdd}
                                    onChange={(event) => handleAddInputChange(event, false)}
                                    placeholder={translate('alias.update.placeholder') as string}
                                />
                            </Box>
                            <IconButton
                                size="sm"
                                aria-label="add-name-&-alias"
                                icon={<Icon as={icons.plus} />}
                                onClick={handleAddNewAlias}
                                isDisabled={!toAddAlias || toAddAlias.mainAlias === ''}
                            />
                        </Flex>
                    </Box>
                </ModalBody>

                <ModalFooter>
                    <Button size="sm" variant="outline" mr={3} isDisabled={isAddLoading} onClick={onCloseAdd}>
                        {translate('alias.update.cancel')}
                    </Button>
                    <Button
                        onClick={handleApplyAddNewAlias}
                        size="sm"
                        isLoading={isAddLoading}
                        isDisabled={!toAddAlias || toAddAlias.mainAlias === '' || toAddAlias.aliases.length === 0}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('alias.update.apply')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AliasAddModal;
